/**
 * Stored exports, web-side: the words a definition and its runs are read in,
 * and the editor's draft <-> the API's payload (keepr-api docs/SCHEMA.md §
 * Stored exports). Pure, so the list, the editor and the runs modal say one
 * thing in one vocabulary, and the tests can hold it still.
 *
 * Nothing here decides a rule the API does not: the limits come from the
 * listing's `limits`, the channels from its `channels`. The fallbacks below
 * are the API's own numbers, used only until the listing lands. One
 * exception, the stored-export cap (RETENTION_CAP_DAYS, below), which this
 * client holds itself so it can ship before the API that enforces it.
 */

import { describeSchedule } from './automationSchedule';
import { formatCount, joinMeta } from './counts';
import { normalizeId } from './ids';

/**
 * THE STORED-EXPORT CAP: 30 days (keepr-api KPR-656, owner ruling p03: "Cap
 * stored exports at 30 days (no exception)"). A web-side CEILING, not only a
 * fallback, and the one exception to "nothing here decides a rule the API does
 * not": this form ships BEFORE the API that enforces the cap, and against the
 * old API — whose `limits.maxRetentionDays` still says 90 — it must already
 * offer 1 to 30, which both APIs accept. When the API's own number is lower,
 * that wins. Rejected: waiting for the listing to say 30 — the window between
 * the two deploys would let a person save a 60 the next API refuses to keep.
 */
export const RETENTION_CAP_DAYS = 30;

export const FALLBACK_LIMITS = {
	maxRecipients: 25,
	maxExternalRecipients: 10,
	maxDefinitions: 50,
	defaultRetentionDays: 14,
	maxRetentionDays: RETENTION_CAP_DAYS,
	defaultLinkExpiresDays: 7,
	refusedScheduleUnits: [ 'minute' ],
	maxManualRunsPerHour: 6,
};

export function limitsOf( limits ) {
	const merged = { ...FALLBACK_LIMITS, ...( limits || {} ) };
	const apiMax = Number( merged.maxRetentionDays );
	const maxRetentionDays = Number.isInteger( apiMax ) && apiMax >= 1 ? Math.min( apiMax, RETENTION_CAP_DAYS ) : RETENTION_CAP_DAYS;
	const apiDefault = Number( merged.defaultRetentionDays );
	return {
		...merged,
		maxRetentionDays,
		defaultRetentionDays: Number.isInteger( apiDefault ) && apiDefault >= 1 ? Math.min( apiDefault, maxRetentionDays ) : Math.min( 14, maxRetentionDays ),
	};
}

export const FORMAT_LABELS = { pdf: 'PDF', xlsx: 'Excel', json: 'JSON' };

/** Excel renders item LISTS alone; a record, card or collection is PDF or JSON. */
export const formatsFor = kind => ( kind === 'items' ? [ 'pdf', 'xlsx', 'json' ] : [ 'pdf', 'json' ] );

export const SUBJECT_KIND_OPTIONS = [
	{ value: 'items', label: 'Items' },
	{ value: 'item', label: 'One item' },
	{ value: 'card', label: 'Card definition' },
	{ value: 'collection', label: 'Collection' },
];

/** Why the SYSTEM switched a definition off (`disabledReason`). */
export const DISABLED_REASON_WORDS = {
	'owner-lost-access': 'Switched off because its owner no longer has access to this collection.',
	'collection-archived': 'Switched off because the collection was archived.',
	'collection-deleted': 'Switched off because the collection was deleted.',
};

/** Why a run produced nothing (`run.skipped`). */
export const SKIP_REASON_WORDS = {
	'owner-lost-access': 'its owner no longer has access to this collection',
	'owner-unverified': 'its owner has not confirmed their email address',
	'collection-archived': 'the collection is archived',
	'collection-deleted': 'the collection was deleted',
	'subject-pending': 'the item it exports is waiting for review; accept it before exporting it',
};

/** Why one recipient was not sent a link (`delivery.suppressed`). */
export const SUPPRESSION_WORDS = {
	'user-opted-out': 'they turned export emails off',
	'no-address': 'their account has no usable address',
	'channel-off': 'email is switched off on this export',
	budget: 'this collection reached its 100 emails an hour',
	'owner-budget': 'the owner reached their email allowance',
	'channel-not-built': 'this channel is not available',
	// A stop link opts the address out of every export this owner sends, and
	// nothing in keepr can lift it — only a new owner can reach them.
	'opted-out': 'they asked, from an email, to stop receiving this owner\'s exports; keepr cannot undo that',
	'recipient-lost-access': 'they no longer have access to this collection',
	// keepr's own suppression list (keepr-api utils/emailEvents/suppressions.js).
	'address-suppressed': 'keepr has stopped mailing that address: it bounced, was reported as spam, or an administrator stopped it',
};

/** A channel that cannot deliver, in one line (`channels.<name>.reason`). */
export const CHANNEL_REASON_WORDS = {
	channel_not_built: 'Not available: keepr has no text-message provider yet, so email carries the link.',
	channel_not_configured: 'Email is not set up on this server, so nothing will be sent until it is.',
};

export const RUN_STATUS_LABELS = {
	pending: 'Running',
	succeeded: 'Succeeded',
	failed: 'Failed',
	skipped: 'Skipped',
	unknown: 'Unknown',
};

export const RUN_STATUS_COLORS = {
	succeeded: 'green',
	failed: 'red',
	skipped: 'default',
	pending: 'gold',
	unknown: 'default',
};

export const DELIVERY_STATUS_LABELS = { sent: 'Sent', failed: 'Failed', suppressed: 'Not sent', skipped: 'Not sent' };

/** What the export is OF, in words. */
export function describeSubject( subject, { cardsById = {}, savedFilters = [] } = {} ) {
	const kind = subject?.kind || 'items';
	const cardName = id => cardsById?.[ normalizeId( id ) ]?.name || 'a card';
	if ( kind === 'collection' ) return 'The collection';
	if ( kind === 'card' ) return `Card definition: ${ cardName( subject.card_id ) }`;
	if ( kind === 'item' ) return 'One item';
	if ( subject?.saved_filter_id ) {
		const filter = ( savedFilters || [] ).find( row => normalizeId( row?._id ) === normalizeId( subject.saved_filter_id ) );
		return filter?.name ? `Saved filter: ${ filter.name }` : 'A saved filter';
	}
	const params = subject?.params || {};
	const base = params.card_id ? `${ cardName( params.card_id ) } items` : 'Every item';
	return params.q ? `${ base }, filtered` : base;
}

/** When it runs, in words — the automations list's own sentence. */
export const describeWhen = definition => ( definition?.schedule ? describeSchedule( definition.schedule ) : 'On demand' );

/** Who it reaches — including nobody. */
export function describeRecipients( definition ) {
	const recipients = definition?.delivery?.recipients || [];
	const emailOn = definition?.delivery?.channels?.email?.enabled !== false;
	if ( ! recipients.length ) return 'Kept for download · nobody is emailed';
	const count = formatCount( recipients.length, 'recipient' );
	return emailOn ? `Emailed to ${ count }` : `${ count } · email is off`;
}

/** The row's second line: what, how, when. */
export const describeDefinition = ( definition, context ) => joinMeta(
	describeSubject( definition?.subject, context ),
	FORMAT_LABELS[ definition?.format ] || ( definition?.format || '' ).toUpperCase(),
	describeWhen( definition ),
);

export const isHexId = value => /^[0-9a-f]{24}$/i.test( `${ value || '' }` );

// Deliberately loose, as the API's is: the delivery proves an address.
export const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/**
 * The editor's draft. Recipients are split into the two things a person adds
 * differently — accounts (picked) and addresses (typed) — and joined again on
 * the way out.
 */
export function draftFromDefinition( definition, { limits, defaultSchedule = null, defaultRecipients = [] } = {} ) {
	const bounds = limitsOf( limits );
	const subject = definition?.subject || { kind: 'items', params: {} };
	const recipients = definition?.delivery?.recipients || defaultRecipients;
	// A definition saved under the old 90-day cap opens AT the cap, and says
	// so (`retentionClampedFrom`): saving it — or a duplicate of it — then
	// sends a number both APIs accept, and the person sees the change before
	// they make it rather than a refusal after. Its files are kept 30 days
	// already, whatever the stored number says (the API caps every artifact).
	const storedRetention = definition?.retentionDays ?? bounds.defaultRetentionDays;
	const overCap = Number.isInteger( storedRetention ) && storedRetention > bounds.maxRetentionDays;
	return {
		name: definition?.name || '',
		subjectKind: subject.kind || 'items',
		itemsSource: subject.saved_filter_id ? 'filter' : 'params',
		savedFilterId: subject.saved_filter_id ? normalizeId( subject.saved_filter_id ) : '',
		params: { ...( subject.params || {} ) },
		itemCardId: '',
		itemId: subject.item_id ? normalizeId( subject.item_id ) : '',
		cardId: subject.card_id ? normalizeId( subject.card_id ) : '',
		format: definition?.format || 'pdf',
		// A definition that STATES a cadence keeps it (null = on demand) — a
		// stored one, or a duplicate; a blank or prefilled one opens on the
		// editor's default.
		schedule: definition && Object.prototype.hasOwnProperty.call( definition, 'schedule' )
			? ( definition.schedule || null )
			: defaultSchedule,
		retentionDays: overCap ? bounds.maxRetentionDays : storedRetention,
		retentionClampedFrom: overCap ? storedRetention : null,
		linkExpiresDays: definition?.delivery?.linkExpiresDays ?? bounds.defaultLinkExpiresDays,
		accountIds: recipients.filter( r => r?.type === 'user' ).map( r => normalizeId( r.userId ) ).filter( Boolean ),
		addresses: recipients.filter( r => r?.type === 'email' ).map( r => ( { address: r.address, ...( r.label ? { label: r.label } : {} ) } ) ),
		emailEnabled: definition?.delivery?.channels?.email?.enabled !== false,
	};
}

/** The subject the draft names, in the API's shape. */
export function subjectFromDraft( draft ) {
	switch ( draft.subjectKind ) {
		case 'collection': return { kind: 'collection' };
		case 'card': return { kind: 'card', card_id: draft.cardId };
		case 'item': return { kind: 'item', item_id: draft.itemId };
		default: {
			if ( draft.itemsSource === 'filter' ) return { kind: 'items', saved_filter_id: draft.savedFilterId };
			const params = {};
			Object.entries( draft.params || {} ).forEach( ( [ key, value ] ) => {
				if ( value !== undefined && value !== null && value !== '' ) params[ key ] = value;
			} );
			return { kind: 'items', params };
		}
	}
}

/**
 * Everything the API would refuse that the editor can see first, as
 * `{ field: sentence }`. The API re-checks all of it; these exist so the
 * refusal lands under the control it names while the author is looking.
 */
export function validateDraft( draft, { limits } = {} ) {
	const bounds = limitsOf( limits );
	const errors = {};
	const name = ( draft.name || '' ).trim();
	if ( ! name ) errors.name = 'Give the export a name — it is how the list and the email refer to it.';
	else if ( name.length > 100 ) errors.name = 'Keep the name to 100 characters.';

	if ( draft.subjectKind === 'items' && draft.itemsSource === 'filter' && ! draft.savedFilterId ) {
		errors.subject = 'Choose a saved filter.';
	}
	if ( draft.subjectKind === 'card' && ! draft.cardId ) errors.subject = 'Choose a card definition.';
	if ( draft.subjectKind === 'item' && ! isHexId( draft.itemId ) ) errors.subject = 'Choose an item from the list.';

	if ( ! formatsFor( draft.subjectKind ).includes( draft.format ) ) {
		errors.format = 'Excel is available for a list of items only.';
	}

	if ( draft.schedule && ( bounds.refusedScheduleUnits || [] ).includes( draft.schedule.unit ) ) {
		errors.schedule = 'An export runs at most once an hour. Choose hours or longer.';
	}

	const retention = Number( draft.retentionDays );
	if ( ! Number.isInteger( retention ) || retention < 1 || retention > bounds.maxRetentionDays ) {
		errors.retentionDays = `Keep the file between 1 and ${ bounds.maxRetentionDays } days.`;
	}
	const link = Number( draft.linkExpiresDays );
	if ( ! Number.isInteger( link ) || link < 1 ) errors.linkExpiresDays = 'A link lasts at least 1 day.';

	const total = ( draft.accountIds || [] ).length + ( draft.addresses || [] ).length;
	if ( total > bounds.maxRecipients ) {
		errors.recipients = `An export reaches at most ${ bounds.maxRecipients } recipients.`;
	}
	else if ( ( draft.addresses || [] ).length > bounds.maxExternalRecipients ) {
		errors.recipients = `At most ${ bounds.maxExternalRecipients } of them may be addresses without a keepr account.`;
	}
	return errors;
}

/**
 * The write body. The editor holds the definition's whole state, so a PATCH
 * sends every section it owns — a key left out reads as "leave this alone",
 * the wrong answer for a recipient list somebody just emptied. `enabled` is
 * never sent from here: the list's switch owns it.
 *
 * RCS is sent as off, always: the API refuses `true` (400 channel_not_built)
 * and the editor draws it unavailable.
 */
export function payloadFromDraft( draft, options ) {
	const retentionDays = Number( draft.retentionDays );
	return {
		name: ( draft.name || '' ).trim(),
		subject: subjectFromDraft( draft ),
		format: draft.format,
		options: options || {},
		schedule: draft.schedule || null,
		retentionDays,
		delivery: {
			recipients: [
				...( draft.accountIds || [] ).map( userId => ( { type: 'user', userId } ) ),
				...( draft.addresses || [] ).map( entry => ( {
					type: 'email',
					address: entry.address,
					...( entry.label ? { label: entry.label } : {} ),
				} ) ),
			],
			channels: { email: { enabled: draft.emailEnabled !== false }, rcs: { enabled: false } },
			// A link never outlives its file; the API clamps too, and this is so
			// the form never SENDS a number it has already drawn as impossible.
			linkExpiresDays: Math.min( Number( draft.linkExpiresDays ), retentionDays ),
		},
	};
}

/**
 * The API's refusal, placed: which editor section a coded 400/409 belongs
 * under. Anything unrecognized goes to the foot of the form, verbatim.
 */
export function placeExportError( error ) {
	const data = error?.response?.data || {};
	const status = error?.response?.status;
	const message = data.message || 'Unable to save this export.';
	const code = data.code;
	if ( [
		'too_many_recipients',
		'too_many_external_recipients',
		'unknown_recipient',
		'recipient_not_collaborator',
		'recipient_opted_out',
	].includes( code ) ) {
		return { field: 'recipients', message };
	}
	if ( code === 'schedule_too_frequent' ) return { field: 'schedule', message };
	// The cap (KPR-656): from an API whose limits this client had not read yet.
	if ( code === 'retention_over_cap' || /^retentionDays/.test( message ) ) return { field: 'retentionDays', message };
	if ( code === 'subject_pending_review' ) return { field: 'subject', message };
	if ( code === 'channel_not_built' ) return { field: 'channels', message };
	if ( status === 409 && /name/i.test( message ) ) return { field: 'name', message };
	if ( /^subject/.test( message ) ) return { field: 'subject', message };
	if ( /^delivery\.recipients/.test( message ) ) return { field: 'recipients', message };
	if ( /^schedule/.test( message ) ) return { field: 'schedule', message };
	if ( status === 403 && ! data.message ) {
		return { field: null, message: 'Only a manager signed in to keepr, with a confirmed email address, can change exports.' };
	}
	return { field: null, message };
}

/** Bytes for a person. */
export function formatBytes( bytes ) {
	const size = Number( bytes ) || 0;
	if ( ! size ) return null;
	if ( size < 1024 ) return `${ size } B`;
	if ( size < 1024 * 1024 ) return `${ Math.round( size / 1024 ) } KB`;
	return `${ ( size / ( 1024 * 1024 ) ).toFixed( 1 ) } MB`;
}

/**
 * The people an export may be sent to by ACCOUNT: the collection's owner and
 * everyone holding collection-wide access — a person's own grant, or a
 * group's — at read or above. A card-scoped or filtered grant does not
 * qualify (the API answers 400 `recipient_not_collaborator`), because a run
 * renders the whole subject and mails it to them.
 *
 * `sharing` is GET /api/collections/{id}/sharing: `{ owner, groups, grants }`.
 * Returns public user objects, de-duplicated, owner first.
 */
export function eligibleAccountRecipients( sharing ) {
	const byId = new Map();
	const add = user => {
		const id = normalizeId( user?._id );
		if ( id && ! byId.has( id ) ) byId.set( id, user );
	};
	if ( sharing?.owner ) add( sharing.owner );
	const collectionWide = grant => grant && ! grant.card_id && ! grant.query
		&& [ 'read', 'write', 'manage' ].includes( grant.role );
	const groupsById = new Map( ( sharing?.groups || [] ).map( group => [ normalizeId( group?._id ), group ] ) );
	( sharing?.grants || [] ).filter( collectionWide ).forEach( grant => {
		if ( grant.principalType === 'user' ) {
			add( grant.user || { _id: grant.principalId } );
		}
		else if ( grant.principalType === 'group' ) {
			( groupsById.get( normalizeId( grant.principalId ) )?.members || [] ).forEach( add );
		}
	} );
	return [ ...byId.values() ];
}
